import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    forwardRef,
    input,
    signal,
    Signal,
    viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { uniqueId } from '@co-3/ctrl-ui/common';

@Component({
    selector: 'app-collapsible-checkbox[label]',
    templateUrl: './collapsible-checkbox.component.html',
    styleUrls: ['./collapsible-checkbox.component.scss'],
    imports: [NgClass, FormsModule],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CollapsibleCheckboxComponent), multi: true },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollapsibleCheckboxComponent implements ControlValueAccessor {
    public label = input<string>();

    private checkbox = viewChild<ElementRef<HTMLInputElement>>('checkbox');

    protected id = uniqueId('collapsible-checkbox');
    protected expanded = signal(true);
    protected showCollapseButton = signal(false);

    private onChanges: (val: boolean) => void;
    protected onTouched: () => void;

    protected labelText: Signal<string> = computed(() => `Mehr Information zu ${this.label}`);

    protected handleCheckboxToggle(e: Event): void {
        const t = e.target as HTMLInputElement;
        const checked = t.checked;

        this.toggleCollapse(checked);
        this.onChanges?.(checked);
        this.onTouched?.();
    }

    protected onButtonClick(): void {
        this.expanded.update((value) => !value);
        this.showCollapseButton.set(true);
    }

    private toggleCollapse(value: boolean): void {
        this.expanded.set(!value);
        this.showCollapseButton.set(value);
    }

    // #region ControlValueAccessor interface

    public writeValue(value: boolean): void {
        const checkbox = this.checkbox()?.nativeElement;
        if (checkbox) {
            checkbox.checked = value;
            this.toggleCollapse(value);
        }
    }
    public registerOnChange(fn: (val: boolean) => void): void {
        this.onChanges = fn;
    }
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
    public setDisabledState(isDisabled: boolean): void {
        const checkbox = this.checkbox()?.nativeElement;
        if (checkbox) {
            checkbox.disabled = isDisabled;
        }
    }

    // #endregion
}
